import { useRef, useState } from 'react'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'

dayjs.extend(customParseFormat)

/**
 * CC-49824: a date input that ALWAYS displays MM/DD/YYYY.
 *
 * Native <input type="date"> holds an ISO value but renders it in the
 * browser/OS locale — V1 forces US format, so V2's filters looked like
 * DD/MM/YYYY on non-US machines. This control shows a US-formatted text
 * field (typing accepted: 7/4/2026, 07/04/2026, 2026-07-04) plus a calendar
 * button that opens the native picker, and emits ISO YYYY-MM-DD like the
 * native input did — a drop-in swap for report filter dates.
 */
const ACCEPTED = ['MM/DD/YYYY', 'M/D/YYYY', 'M/DD/YYYY', 'MM/D/YYYY', 'YYYY-MM-DD']

const toUs = (iso) => (iso && dayjs(iso, 'YYYY-MM-DD', true).isValid()
  ? dayjs(iso, 'YYYY-MM-DD', true).format('MM/DD/YYYY')
  : '')

const parseUs = (text) => {
  const t = String(text ?? '').trim()
  if (!t) return ''
  for (const f of ACCEPTED) {
    const d = dayjs(t, f, true)
    if (d.isValid()) return d.format('YYYY-MM-DD')
  }
  return null // incomplete / invalid
}

const UsDateInput = ({ value = '', onChange, className = '', ...inputProps }) => {
  const [text, setText] = useState(() => toUs(value))
  const [focused, setFocused] = useState(false)
  const nativeRef = useRef(null)

  // Follow external value changes (view apply, reset, quick-range presets) —
  // but never clobber the text mid-typing. Adjust-during-render (the React
  // "derived state" pattern) instead of an effect: no extra commit, no
  // set-state-in-effect cascade.
  const [lastValue, setLastValue] = useState(value)
  if (value !== lastValue) {
    setLastValue(value)
    if (!focused) setText(toUs(value))
  }

  const commit = (raw) => {
    const iso = parseUs(raw)
    if (iso === '') onChange?.('')
    else if (iso) onChange?.(iso)
    // null = incomplete input: keep typing, don't emit
  }

  const openPicker = () => {
    const el = nativeRef.current
    if (!el) return
    try {
      if (typeof el.showPicker === 'function') el.showPicker()
      else el.focus()
    } catch {
      el.focus() // showPicker throws without a user gesture in some browsers
    }
  }

  return (
    <span className="relative inline-flex w-full">
      <input
        type="text"
        inputMode="numeric"
        placeholder="MM/DD/YYYY"
        value={text}
        onFocus={() => setFocused(true)}
        onChange={(e) => {
          setText(e.target.value)
          commit(e.target.value)
        }}
        onBlur={(e) => {
          setFocused(false)
          const iso = parseUs(e.target.value)
          // Snap the display to the canonical format (or back to the last
          // valid value when the text can't parse).
          setText(iso ? toUs(iso) : iso === '' ? '' : toUs(value))
          if (iso === '') onChange?.('')
          else if (iso) onChange?.(iso)
        }}
        className={`${className} pr-7`}
        {...inputProps}
      />
      <button
        type="button"
        onClick={openPicker}
        aria-label={`${inputProps['aria-label'] || 'Date'} — open calendar`}
        className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600"
        data-print-hide
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3.75 18.75V7.5a2.25 2.25 0 012.25-2.25h12a2.25 2.25 0 012.25 2.25v11.25m-18 0A2.25 2.25 0 006 21h12a2.25 2.25 0 002.25-2.25m-18 0v-7.5A2.25 2.25 0 016 9h12a2.25 2.25 0 012.25 2.25v7.5" />
        </svg>
      </button>
      {/* The native control supplies the calendar UI only — visually hidden,
          skipped by the tab order (the text input is the accessible field). */}
      <input
        ref={nativeRef}
        type="date"
        tabIndex={-1}
        aria-hidden="true"
        value={value || ''}
        onChange={(e) => {
          setText(toUs(e.target.value))
          onChange?.(e.target.value)
        }}
        className="absolute right-0 bottom-0 w-px h-px opacity-0 pointer-events-none"
      />
    </span>
  )
}

export default UsDateInput
